// Client half of the ESPN name lookup. Every call is best-effort: a failure returns an empty list
// plus a reason so the builder can say so and keep the manual path working, and nothing here is ever
// required for a slip to price.
import { normalizeLegTag, remapLegSides, sideKeyForName } from "./pricing.js";

const CACHE_LIMIT = 24;
const slateCache = new Map();
const rosterCache = new Map();

export const MANUAL_GAME_ID = "";
export const ESPN_UNAVAILABLE = "Games could not be loaded — enter team names manually.";

export function todayIso(now = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function remember(cache, key, value) {
  cache.set(key, value);
  while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  return value;
}

async function loadJson(url, cache, key, signal) {
  if (cache.has(key)) return cache.get(key);
  const response = await fetch(url, { signal });
  const body = await response.json();
  if (!response.ok) return { ...body, ok: false };
  return remember(cache, key, { ...body, ok: true });
}

export async function loadEspnGames(sport, date, { signal } = {}) {
  const key = `${sport}|${date}`;
  try {
    const body = await loadJson(
      `/api/espn/games?sport=${encodeURIComponent(sport)}&date=${encodeURIComponent(date)}`,
      slateCache,
      key,
      signal,
    );
    const games = Array.isArray(body.games) ? body.games : [];
    return { games, reason: games.length === 0 ? body.reason ?? null : null };
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return { games: [], reason: ESPN_UNAVAILABLE };
  }
}

export async function loadEspnRoster(sport, team, { signal } = {}) {
  const key = `${sport}|${team}`;
  try {
    const body = await loadJson(
      `/api/espn/roster?sport=${encodeURIComponent(sport)}&team=${encodeURIComponent(team)}`,
      rosterCache,
      key,
      signal,
    );
    return { players: Array.isArray(body.players) ? body.players : [], reason: body.reason ?? null };
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return { players: [], reason: null };
  }
}

// name -> the roster fact used to auto-assign and lock a leg's team. First writer wins, so a name
// shared by both squads keeps a stable, reproducible assignment instead of flipping between loads.
export function rosterIndex(entries) {
  const index = new Map();
  for (const entry of entries) {
    const key = normalizeLegTag(entry.name);
    if (!key || index.has(key)) continue;
    index.set(key, {
      playerId: String(entry.id ?? ""),
      name: String(entry.name ?? ""),
      position: String(entry.position ?? ""),
      team: String(entry.team ?? ""),
      teamName: String(entry.teamName ?? ""),
    });
  }
  return index;
}

export function rosterEntries(rosters) {
  return rosters.flatMap(({ team, players }) => players.map((player) => ({
    ...player,
    team: team.abbrev || team.name,
    teamName: team.name || team.abbrev,
  }))).sort((a, b) => a.name.localeCompare(b.name));
}

// The roster lock lifecycle, kept out of the component so it can be reasoned about and tested.
// A roster hit fixes the player's identity AND which side of the game he is on; anything the user
// then does to the side control is a manual override that releases the lock. The side is one of the
// group's two, so a roster hit can never introduce a team the group does not have.
export function playerPatch(index, value, game) {
  const match = index.get(normalizeLegTag(value));
  if (!match) return { player: value, playerId: "", teamLock: null };
  const side = sideKeyForName(game, match.team, match.teamName);
  if (!side) return { player: value, playerId: match.playerId, teamLock: null };
  return {
    player: value,
    playerId: match.playerId,
    side,
    teamLock: { teamName: match.teamName || match.team, position: match.position },
  };
}

export function unlockPatch() {
  return { teamLock: null };
}

export function espnGameTeams(game) {
  if (!game?.home || !game?.away) return [];
  return [game.away, game.home].map((team) => ({
    id: String(team.id ?? ""),
    name: String(team.name ?? ""),
    abbrev: String(team.abbrev ?? "") || String(team.name ?? ""),
  }));
}

// Away first, matching espnGameTeams, so "Team A" and the first roster are the same side.
export function espnGameSides(game) {
  const [away, home] = espnGameTeams(game);
  return { a: away?.name ?? "", b: home?.name ?? "" };
}

// Picking a listed game fills BOTH sides and locks them; going back to "not listed" only unlocks,
// leaving the names in place so the legs pointing at them stay valid.
export function pickGamePatch(game, chosen) {
  if (!chosen) return { espnGameId: "", teams: [], sidesLocked: false };
  const sides = espnGameSides(chosen);
  return {
    espnGameId: String(chosen.id ?? ""),
    teams: espnGameTeams(chosen),
    sides,
    sidesLocked: true,
    legs: remapLegSides(game, sides),
  };
}

export function resetEspnCaches() {
  slateCache.clear();
  rosterCache.clear();
}
